/**
 * Floating button scrolling the page back to the top.
 */

import { query } from "../core/dom";

const VISIBLE_CLASS = "is-visible";

/** How far the visitor has to scroll before the button appears. */
export const SCROLL_THRESHOLD = 600;

/** Return whether the visitor asked the system for reduced motion. */
export function prefersReducedMotion(): boolean {
    if (typeof window.matchMedia !== "function") {
        return false;
    }

    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Toggle the visibility class of `button` for the given scroll offset. */
export function updateVisibility(button: HTMLElement, offset: number): boolean {
    const visible = offset > SCROLL_THRESHOLD;

    button.classList.toggle(VISIBLE_CLASS, visible);
    button.setAttribute("aria-hidden", visible ? "false" : "true");

    return visible;
}

/** Scroll the window back to the top, honouring the motion preference. */
export function scrollToTop(): void {
    window.scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

/** Wire the floating button scrolling the page back to the top. */
export function initBackToTop(): void {
    const button = query<HTMLElement>("[data-back-to-top]");
    if (!button) {
        return;
    }

    updateVisibility(button, window.scrollY);

    window.addEventListener(
        "scroll",
        () => {
            updateVisibility(button, window.scrollY);
        },
        { passive: true },
    );

    button.addEventListener("click", (event: Event) => {
        event.preventDefault();
        scrollToTop();
    });
}
